import {
  ConstructionStage,
  LongLeadEquipmentStatus,
  NoticeToProceedStatus,
} from '@/types/compliance';
import {
  BatteryConfiguration,
  ChargeRestriction,
  ChargingSource,
  CompanyCreditProfile,
  CompanyRole,
  ISO,
  PRODUCT_TYPE,
  PROJECT_OPERATIONAL_STATUS,
  PriceUnit,
  RegulatoryStatus,
  SettlementIndex,
  StorageTechnology,
  UserRole,
} from '@/utils/enums';

import { baseApi } from './baseApi';
import { ApiTags, ReferenceTagIds } from './tags';

export type AvailableUserRole = {
  id: UserRole;
  name: string;
  description: string;
};

export type AvailableUserRoles = AvailableUserRole[];

export type ReferenceUserRole = {
  id: CompanyRole;
  name: string;
  availableRoles: AvailableUserRoles;
};

export type ReferenceUserRoles = ReferenceUserRole[];

export type RegulatoryStatusDto = {
  id: RegulatoryStatus;
  name: string;
  description: string;
};

export type CreditProfileDto = {
  id: CompanyCreditProfile;
  name: string;
  description: string;
};

export type CompanyRoleDto = {
  id: CompanyRole;
  name: string;
  description: string;
};

export type CountryDto = {
  id: number;
  name: string;
  codeIsoAlpha2: string;
  codeIsoAlpha3: string;
};

export type StateDto = {
  id: number;
  countryId: number;
  name: string;
  abbreviation: string;
};

export type ProjectReferenceDto = {
  id: string;
  name: string;
};

export type ProductTypeDto = {
  id: string;
  name: string;
  requiresSefConsent: boolean;
};

export type SettlementIndexDto = {
  id: string;
  name: string;
  symbol: string;
};

export type HubZoneMatrixDto = {
  id: number;
  iso: ISO;
  settlementPoint: string;
  settlementIndex: SettlementIndex;
};

export type HubZonesParams = {
  iso: ISO | ISO[];
};

export type ContractTypesParams = {
  productType?: PRODUCT_TYPE;
  projectOperationalStatus?: PROJECT_OPERATIONAL_STATUS;
};

export type ContractCreditProfilesParams = {
  companyCreditProfile?: CompanyCreditProfile;
  side?: 'BID';
};

export type AdvisorRoleSearchParams = {
  companyRole: CompanyRole;
};

export type CountyDto = {
  id: number;
  stateId: number;
  name: string;
};

export type PanelTypeDto = {
  id: string;
  name: string;
};

export type PanelTechnologyDto = {
  id: string;
  name: string;
};

export type TrackingTypeDto = {
  id: string;
  name: string;
};

export type IsoDto = {
  id: string;
  name: string;
};

export type HubZoneDto = {
  id: number;
  settlementPoint: string;
  isoId: string;
};

export type InterconnectionPointDto = {
  id: number;
  name: string;
  isoId: string;
};

export type InterconnectionVoltageDto = {
  id: string;
  name: string;
};

export type ComplianceStatusDto = {
  id: string;
  name: string;
};

/**
 * An option from an enum reference endpoint (`ProjectReferenceDTO`), with the id
 * narrowed to the backend enum it comes from. `name` is the backend label.
 */
export type EnumReferenceDto<T extends string> = {
  id: T;
  name: string;
};

export type BatteryConfigurationDto = EnumReferenceDto<BatteryConfiguration>;
export type ChargingSourceDto = EnumReferenceDto<ChargingSource>;
export type ChargeRestrictionDto = EnumReferenceDto<ChargeRestriction>;
export type StorageTechnologyDto = EnumReferenceDto<StorageTechnology>;
export type ConstructionStageDto = EnumReferenceDto<ConstructionStage>;
export type NoticeToProceedStatusDto = EnumReferenceDto<NoticeToProceedStatus>;
export type LongLeadEquipmentStatusDto =
  EnumReferenceDto<LongLeadEquipmentStatus>;
export type PriceUnitDto = EnumReferenceDto<PriceUnit>;

export type BidStatusDto = {
  id: string;
  name: string;
  description: string;
};

export type MarketStatusDto = {
  serverTime: string;
  nextOpen: string;
  nextClose: string;
  marketTimezone: string;
};

export type TechnologiesDto = {
  id: string;
  name: string;
  description: string;
  isVisible: boolean;
};

export type ContractingFieldDto = {
  fieldKey: string;
  dataType: string;
  description: string;
};

/** An external document whose host protects it with a password. */
export type ProtectedDocument = 'FEE_SCHEDULE' | 'PROTECTED_PAGES';

export type DocumentPasswordDto = {
  document: ProtectedDocument;
  password: string;
};

export type AppVersionDto = {
  version: string;
};

const BASE_URL = '/api/v1/reference';

export const referenceApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getReferenceUserRoles: build.query<ReferenceUserRoles, void>({
      query: () => `${BASE_URL}/user-roles`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.USER_ROLES },
      ],
    }),
    getRegulatoryStatuses: build.query<RegulatoryStatusDto[], void>({
      query: () => `${BASE_URL}/regulatory-statuses`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.REGULATORY_STATUSES },
      ],
    }),
    getCreditProfiles: build.query<CreditProfileDto[], void>({
      query: () => `${BASE_URL}/credit-profiles`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.CREDIT_PROFILES },
      ],
    }),
    getCompanyRoles: build.query<CompanyRoleDto[], void>({
      query: () => `${BASE_URL}/company-roles`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.COMPANY_ROLES },
      ],
    }),
    getCountries: build.query<CountryDto[], void>({
      query: () => `${BASE_URL}/countries`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.COUNTRIES },
      ],
    }),
    getStates: build.query<StateDto[], number>({
      query: countryId => `${BASE_URL}/countries/${countryId}/states`,
      providesTags: (result, error, countryId) => [
        {
          type: ApiTags.REFERENCES,
          id: `${ReferenceTagIds.STATES}-${countryId}`,
        },
      ],
    }),
    getCounties: build.query<CountyDto[], number>({
      query: stateId => `${BASE_URL}/states/${stateId}/counties`,
      providesTags: (result, error, stateId) => [
        {
          type: ApiTags.REFERENCES,
          id: `${ReferenceTagIds.COUNTIES}-${stateId}`,
        },
      ],
    }),
    getAdvisorRolesByCompanyRole: build.query<
      ReferenceUserRole,
      AdvisorRoleSearchParams
    >({
      query: params => {
        const searchParams = new URLSearchParams();
        searchParams.append('companyRole', params.companyRole);
        return `${BASE_URL}/advisor-roles?${searchParams.toString()}`;
      },
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.ADVISOR_ROLES,
        },
      ],
    }),
    getPanelTypes: build.query<PanelTypeDto[], void>({
      query: () => `${BASE_URL}/panel-types`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.PANEL_TYPES },
      ],
    }),
    getTechnologies: build.query<TechnologiesDto[], boolean | void>({
      query: all => ({
        url: `${BASE_URL}/technologies`,
        params: all
          ? {
              all: true,
            }
          : undefined,
      }),
      providesTags: (result, error, panelType) => [
        {
          type: ApiTags.REFERENCES,
          id: `${ReferenceTagIds.TECHNOLOGIES}-${panelType}`,
        },
      ],
    }),
    getPanelTechnologies: build.query<PanelTechnologyDto[], string>({
      query: panelType =>
        `${BASE_URL}/panel-technologies?panelType=${panelType}`,
      providesTags: (result, error, panelType) => [
        {
          type: ApiTags.REFERENCES,
          id: `${ReferenceTagIds.PANEL_TECHNOLOGIES}-${panelType}`,
        },
      ],
    }),
    getTrackingTypes: build.query<TrackingTypeDto[], void>({
      query: () => `${BASE_URL}/tracking-types`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.TRACKING_TYPES },
      ],
    }),
    getOfferStatuses: build.query<ProjectReferenceDto[], void>({
      query: () => `${BASE_URL}/offer-statuses`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.OFFER_STATUSES },
      ],
    }),
    getProductTypes: build.query<ProductTypeDto[], void>({
      query: () => `${BASE_URL}/product-types`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.PRODUCT_TYPES },
      ],
    }),
    getContractTypes: build.query<ProjectReferenceDto[], ContractTypesParams>({
      query: ({ productType, projectOperationalStatus }) => {
        const searchParams = new URLSearchParams();

        if (productType) {
          searchParams.append('productType', productType);
        }

        if (projectOperationalStatus) {
          searchParams.append(
            'projectOperationalStatus',
            projectOperationalStatus
          );
        }

        return `${BASE_URL}/contract-types?${searchParams.toString()}`;
      },
      providesTags: (result, error, { productType }) => [
        {
          type: ApiTags.REFERENCES,
          id: `${ReferenceTagIds.CONTRACT_TYPES}-${productType}`,
        },
      ],
    }),
    getContractCreditProfiles: build.query<
      ProjectReferenceDto[],
      ContractCreditProfilesParams
    >({
      query: ({ companyCreditProfile, side }) => {
        const searchParams = new URLSearchParams();

        if (companyCreditProfile) {
          searchParams.append('companyCreditProfile', companyCreditProfile);
        }

        if (side) {
          searchParams.append('side', side);
        }

        return `${BASE_URL}/contract-credit-profiles?${searchParams.toString()}`;
      },
      providesTags: (result, error, { companyCreditProfile, side }) => [
        {
          type: ApiTags.REFERENCES,
          id: `${ReferenceTagIds.CONTRACT_CREDIT_PROFILES}-${companyCreditProfile}-${side}`,
        },
      ],
    }),
    getNegativePricing: build.query<ProjectReferenceDto[], void>({
      query: () => `${BASE_URL}/negative-pricing`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.NEGATIVE_PRICING },
      ],
    }),
    getApplicableRecPrograms: build.query<ProjectReferenceDto[], void>({
      query: () => `${BASE_URL}/applicable-rec-programs`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.APPLICABLE_REC_PROGRAMS,
        },
      ],
    }),
    getPerformanceGuaranteeMethods: build.query<ProjectReferenceDto[], void>({
      query: () => `${BASE_URL}/performance-guarantee-methods`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.PERFORMANCE_GUARANTEE_METHODS,
        },
      ],
    }),
    getApplicableTrackingSystems: build.query<ProjectReferenceDto[], void>({
      query: () => `${BASE_URL}/applicable-tracking-systems`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.APPLICABLE_TRACKING_SYSTEMS,
        },
      ],
    }),
    getRecDeliveryMethodologies: build.query<ProjectReferenceDto[], void>({
      query: () => `${BASE_URL}/rec-delivery-methodologies`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.REC_DELIVERY_METHODOLOGIES,
        },
      ],
    }),
    getIsos: build.query<ProjectReferenceDto[], void>({
      query: () => `${BASE_URL}/isos`,
      providesTags: [{ type: ApiTags.REFERENCES, id: ReferenceTagIds.ISOS }],
    }),
    getSettlementIndexes: build.query<SettlementIndexDto[], void>({
      query: () => `${BASE_URL}/settlement-indexes`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.SETTLEMENT_INDEXES },
      ],
    }),
    getHubZones: build.query<HubZoneMatrixDto[], HubZonesParams>({
      query: ({ iso }) => {
        const isoArray = Array.isArray(iso) ? iso : [iso];

        const searchParams = new URLSearchParams();

        isoArray.forEach(iso => searchParams.append('iso', iso));

        return `${BASE_URL}/hub-zones?${searchParams.toString()}`;
      },
      providesTags: (result, error, { iso }) => {
        const isoArray = Array.isArray(iso) ? iso : [iso];

        return [
          {
            type: ApiTags.REFERENCES,
            id: `${ReferenceTagIds.HUB_ZONES}-${isoArray.join('-')}`,
          },
        ];
      },
    }),
    getInterconnectionPoints: build.query<InterconnectionPointDto[], string>({
      query: iso => `${BASE_URL}/interconnection-points?iso=${iso}`,
      providesTags: (result, error, iso) => [
        {
          type: ApiTags.REFERENCES,
          id: `${ReferenceTagIds.INTERCONNECTION_POINTS}-${iso}`,
        },
      ],
    }),
    getInterconnectionVoltages: build.query<InterconnectionVoltageDto[], void>({
      query: () => `${BASE_URL}/interconnection-voltages`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.INTERCONNECTION_VOLTAGES,
        },
      ],
    }),
    getCompliancePermitStatuses: build.query<ComplianceStatusDto[], void>({
      query: () => `${BASE_URL}/compliance-permit-statuses`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.COMPLIANCE_PERMIT_STATUSES,
        },
      ],
    }),
    getComplianceDocumentStatuses: build.query<ComplianceStatusDto[], void>({
      query: () => `${BASE_URL}/compliance-document-statuses`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.COMPLIANCE_DOCUMENT_STATUSES,
        },
      ],
    }),
    getBatteryConfigurations: build.query<BatteryConfigurationDto[], void>({
      query: () => `${BASE_URL}/battery-configurations`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.BATTERY_CONFIGURATIONS,
        },
      ],
    }),
    getChargingSources: build.query<ChargingSourceDto[], void>({
      query: () => `${BASE_URL}/charging-sources`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.CHARGING_SOURCES },
      ],
    }),
    getChargeRestrictions: build.query<ChargeRestrictionDto[], void>({
      query: () => `${BASE_URL}/charge-restrictions`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.CHARGE_RESTRICTIONS },
      ],
    }),
    getStorageTechnologies: build.query<StorageTechnologyDto[], void>({
      query: () => `${BASE_URL}/storage-technologies`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.STORAGE_TECHNOLOGIES },
      ],
    }),
    getConstructionStages: build.query<ConstructionStageDto[], void>({
      query: () => `${BASE_URL}/construction-stages`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.CONSTRUCTION_STAGES },
      ],
    }),
    getNoticeToProceedStatuses: build.query<NoticeToProceedStatusDto[], void>({
      query: () => `${BASE_URL}/notice-to-proceed-statuses`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.NOTICE_TO_PROCEED_STATUSES,
        },
      ],
    }),
    getLongLeadEquipmentStatuses: build.query<
      LongLeadEquipmentStatusDto[],
      void
    >({
      query: () => `${BASE_URL}/long-lead-equipment-statuses`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.LONG_LEAD_EQUIPMENT_STATUSES,
        },
      ],
    }),
    getBidStatuses: build.query<BidStatusDto[], void>({
      query: () => `${BASE_URL}/bid-statuses`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.BID_STATUSES },
      ],
    }),
    getPriceUnits: build.query<PriceUnitDto[], void>({
      query: () => `${BASE_URL}/price-units`,
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.PRICE_UNITS },
      ],
    }),
    getMarketStatus: build.query<MarketStatusDto, void>({
      query: () => `${BASE_URL}/market/status`,
    }),
    getContractingFields: build.query<ContractingFieldDto[], void>({
      query: () => `${BASE_URL}/contracting-fields`,
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.CONTRACTING_FIELDS,
        },
      ],
    }),
    getDocumentPasswords: build.query<DocumentPasswordDto[], void>({
      // A failure resolves to no passwords rather than rejecting. Every screen with
      // a Fee Schedule link runs this query, and a rejection would reach the global
      // error snackbar on screens where the user did nothing; the links still work
      // without a password, so the view degrades silently instead.
      queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery(`${BASE_URL}/document-passwords`);
        if (result.error) {
          console.error('Could not load document passwords', result.error);
        }
        return result.error
          ? { data: [] }
          : { data: (result.data ?? []) as DocumentPasswordDto[] };
      },
      providesTags: [
        {
          type: ApiTags.REFERENCES,
          id: ReferenceTagIds.DOCUMENT_PASSWORDS,
        },
      ],
    }),
    getAppVersion: build.query<string | null, void>({
      // A failure resolves to no version rather than rejecting, for the same reason as
      // getDocumentPasswords: the user menu runs this on every page, and the menu simply
      // shows no version line without one.
      queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery(`${BASE_URL}/version`);
        if (result.error) {
          console.error('Could not load the deployed version', result.error);
          return { data: null };
        }
        return {
          data: (result.data as AppVersionDto | undefined)?.version ?? null,
        };
      },
      providesTags: [
        { type: ApiTags.REFERENCES, id: ReferenceTagIds.APP_VERSION },
      ],
    }),
  }),
});

export const {
  useGetReferenceUserRolesQuery,
  useGetRegulatoryStatusesQuery,
  useGetCreditProfilesQuery,
  useGetCompanyRolesQuery,
  useGetCountriesQuery,
  useGetStatesQuery,
  useGetCountiesQuery,
  useGetAdvisorRolesByCompanyRoleQuery,
  useGetPanelTypesQuery,
  useGetPanelTechnologiesQuery,
  useGetTrackingTypesQuery,
  useGetInterconnectionPointsQuery,
  useGetInterconnectionVoltagesQuery,
  useGetComplianceDocumentStatusesQuery,
  useGetCompliancePermitStatusesQuery,
  useGetBatteryConfigurationsQuery,
  useGetChargingSourcesQuery,
  useGetChargeRestrictionsQuery,
  useGetStorageTechnologiesQuery,
  useGetConstructionStagesQuery,
  useGetNoticeToProceedStatusesQuery,
  useGetLongLeadEquipmentStatusesQuery,
  useGetOfferStatusesQuery,
  useGetProductTypesQuery,
  useGetContractTypesQuery,
  useGetContractCreditProfilesQuery,
  useGetNegativePricingQuery,
  useGetApplicableRecProgramsQuery,
  useGetPerformanceGuaranteeMethodsQuery,
  useGetApplicableTrackingSystemsQuery,
  useGetRecDeliveryMethodologiesQuery,
  useGetIsosQuery,
  useGetSettlementIndexesQuery,
  useGetHubZonesQuery,
  useGetBidStatusesQuery,
  useGetPriceUnitsQuery,
  useGetTechnologiesQuery,
  useGetMarketStatusQuery,
  useGetContractingFieldsQuery,
  useGetDocumentPasswordsQuery,
  useGetAppVersionQuery,
} = referenceApi;
